'use client';

import { useContext, useEffect, useState } from 'react';
import { GetPdpModalConfigAction, PdpModalConfig } from '../types/cmsTypes';
import { CmsClientContext } from '../utils/cmsPageContext/CmsClientContext';
import { parseUrlAlias } from '../utils/helpers/urlAlias';

// Session-lifetime caches: the PDP page config is static per alias+locale, so
// each key is fetched at most once and concurrent opens share one request.
// Keyed by locale as well — the same alias resolves to a different page per
// locale, and a page may configure a different filter there.
const configCache = new Map<string, PdpModalConfig | undefined>();
const pendingFetches = new Map<string, Promise<PdpModalConfig | undefined>>();

const cacheKeyFor = (lang: string | undefined, url: string) =>
  `${lang ?? ''}|${url}`;

/**
 * Resolves the slim ProductDetails config of the canonical PDP page behind a
 * product's url_alias via the app-provided `getPdpModalConfigAction` (CMS-1924).
 * The action and `isPreview` arrive as props (same convention as
 * `ProductBlockAsideModal`'s `getProductAction` — app-provided fetchers are
 * not context material). `enabled` gates the fetch to while the marker modal
 * is open; a failed fetch or missing action resolves to `undefined` and
 * callers fall back. Failures are never cached, so the next freshly-mounted
 * consumer retries.
 *
 * In preview both caches are bypassed, so an editor who changes the PDP's
 * benefit filter sees it on the next open instead of on the next full reload.
 */
export const usePdpModalConfig = (props: {
  urlAlias: string | undefined;
  enabled: boolean;
  getPdpModalConfigAction: GetPdpModalConfigAction | undefined;
  isPreview?: boolean;
}): { config: PdpModalConfig | undefined; loading: boolean } => {
  const { urlAlias, enabled, getPdpModalConfigAction } = props;
  const isPreview = !!props.isPreview;
  // Context is only consulted for the locale (cache-key data) — read leniently,
  // consumers (and their tests) may render without a CmsClientProvider.
  const context = useContext(CmsClientContext);
  const { url } = parseUrlAlias(urlAlias);
  const canFetch = enabled && !!url && !!getPdpModalConfigAction;
  const cacheKey = cacheKeyFor(context?.lang, url);

  const [fetched, setFetched] = useState<{
    key: string;
    config: PdpModalConfig | undefined;
  }>();

  const fetchedThisKey = fetched?.key === cacheKey;
  const cacheHit = !isPreview && configCache.has(cacheKey);
  // Derived rather than stored: `enabled` only flips to true a tick after mount
  // (the modal reads its open state from the URL hash), so a `loading` state
  // initialised at mount would report `false` on the first enabled render and
  // flash one frame of fallback content before the spinner.
  const isResolved = fetchedThisKey || cacheHit;

  useEffect(() => {
    if (!canFetch || isResolved) {
      return;
    }

    let cancelled = false;
    const request =
      (!isPreview && pendingFetches.get(cacheKey)) ||
      getPdpModalConfigAction!(url).then((result) => {
        // Cache only fulfilled fetches: a cached rejection would pin the
        // fallback for the whole session, indistinguishable from a page that
        // legitimately has no config.
        if (!isPreview) {
          configCache.set(cacheKey, result);
        }
        return result;
      });
    if (!isPreview) {
      pendingFetches.set(cacheKey, request);
    }

    request
      .catch(() => undefined)
      .then((result) => {
        if (!isPreview) {
          pendingFetches.delete(cacheKey);
        }
        if (!cancelled) {
          setFetched({ key: cacheKey, config: result });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [canFetch, isResolved, cacheKey, url, isPreview, getPdpModalConfigAction]);

  // Preview: the modal stays mounted across close/reopen, so the cache bypass
  // alone is not enough — the component-local `fetched` must be cleared on
  // close, or an editor reopening the modal keeps seeing the stale config.
  useEffect(() => {
    if (isPreview && !enabled && fetched) {
      setFetched(undefined);
    }
  }, [isPreview, enabled, fetched]);

  const config = fetchedThisKey
    ? fetched?.config
    : cacheHit
      ? configCache.get(cacheKey)
      : undefined;

  return { config, loading: canFetch && !isResolved };
};
